import React, { useEffect, useState } from 'react';
import { lessonPlansApi } from '../../services/apiV2';

type OperationRow = {
  resource_key: string;
  title: string;
  needs_printing: boolean;
  audience: 'teacher' | 'student' | 'both';
  print_status: 'not_needed' | 'pending' | 'ready';
  print_quantity: number;
  teacher_note: string;
  student_released: boolean;
  link_status: 'unknown' | 'ok' | 'warning' | 'broken';
  link_checked_at?: string | null;
  link_error?: string;
  total_opens: number;
  unique_openers: number;
};

export const ResourceOperationsPanel: React.FC<{
  lessonPlanId: number;
  sectionId: number;
}> = ({ lessonPlanId, sectionId }) => {
  const [rows, setRows] = useState<OperationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState('');
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await lessonPlansApi.resourceOperations(lessonPlanId, sectionId);
      setRows(Array.isArray(response.data?.resources) ? response.data.resources : []);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Unable to load material readiness.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, [lessonPlanId, sectionId]);

  const update = async (row: OperationRow, changes: Partial<OperationRow>) => {
    setBusyKey(row.resource_key);
    setError('');
    try {
      const response = await lessonPlansApi.updateResourceOperation(lessonPlanId, sectionId, {
        resource_key: row.resource_key,
        ...changes,
      });
      setRows(Array.isArray(response.data?.resources) ? response.data.resources : []);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Unable to update material readiness.');
    } finally {
      setBusyKey('');
    }
  };

  const revalidate = async (row: OperationRow) => {
    setBusyKey(row.resource_key);
    setError('');
    try {
      await lessonPlansApi.revalidateResource(lessonPlanId, sectionId, row.resource_key);
      await load();
    } catch (err: any) {
      const data = err?.response?.data;
      setError(data?.resource?.[0] || data?.detail || 'Unable to check this link.');
    } finally {
      setBusyKey('');
    }
  };

  if (loading) return <p className="text-sm text-slate-500">Loading readiness...</p>;
  return (
    <div className="space-y-3">
      {error ? <p role="alert" className="rounded bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p> : null}
      {rows.map((row) => (
        <section key={row.resource_key} className="rounded-lg border border-slate-200 p-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="font-semibold text-slate-950">{row.title}</p>
              <p className="mt-1 text-xs text-slate-500">{row.total_opens} opens · {row.unique_openers} people</p>
            </div>
            <span className={`rounded-full px-2 py-1 text-xs ${row.link_status === 'ok' ? 'bg-emerald-50 text-emerald-800' : row.link_status === 'broken' ? 'bg-rose-50 text-rose-800' : 'bg-slate-100 text-slate-700'}`}>
              Link: {row.link_status}
            </span>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {row.needs_printing ? (
              <label className="text-xs font-medium text-slate-700">Print status
                <select className="mt-1 w-full rounded border border-slate-200 px-2 py-1.5 text-sm" value={row.print_status} disabled={busyKey === row.resource_key} onChange={(event) => void update(row, { print_status: event.target.value as OperationRow['print_status'] })}>
                  <option value="pending">Needs printing</option>
                  <option value="ready">Printed / ready</option>
                  <option value="not_needed">Not needed</option>
                </select>
              </label>
            ) : null}
            {row.needs_printing ? (
              <label className="text-xs font-medium text-slate-700">Quantity
                <input className="mt-1 w-full rounded border border-slate-200 px-2 py-1.5 text-sm" type="number" min={0} value={row.print_quantity} disabled={busyKey === row.resource_key} onChange={(event) => setRows((current) => current.map((item) => item.resource_key === row.resource_key ? { ...item, print_quantity: Number(event.target.value) } : item))} onBlur={() => void update(row, { print_quantity: row.print_quantity })} />
              </label>
            ) : null}
          </div>
          {row.audience !== 'teacher' ? (
            <label className="mt-3 flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={row.student_released} disabled={busyKey === row.resource_key} onChange={(event) => void update(row, { student_released: event.target.checked })} />
              Released to students
            </label>
          ) : null}
          <label className="mt-3 block text-xs font-medium text-slate-700">Teacher note
            <input className="mt-1 w-full rounded border border-slate-200 px-2 py-1.5 text-sm" value={row.teacher_note} disabled={busyKey === row.resource_key} onChange={(event) => setRows((current) => current.map((item) => item.resource_key === row.resource_key ? { ...item, teacher_note: event.target.value } : item))} onBlur={() => void update(row, { teacher_note: row.teacher_note })} placeholder="Optional prep note" />
          </label>
          <button type="button" className="mt-3 rounded border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50" disabled={busyKey === row.resource_key} onClick={() => void revalidate(row)}>
            {busyKey === row.resource_key ? 'Checking...' : 'Check link now'}
          </button>
          {row.link_error ? <p className="mt-1 text-xs text-rose-700">{row.link_error}</p> : null}
        </section>
      ))}
    </div>
  );
};
